// ── STATE ────────────────────────────────────────────────────────────────────
const ixirproEnergyState = {
  cur: 0,
  answers: { homeSize:null, bill:null, symptoms:[], zones:[] }
};

// ── PRODUCT RECOMMENDATIONS ───────────────────────────────────────────────────
// Format:
//   can  → kendi nozzle/tüpü olan, tabancasız kullanım (dar boşluklar, profesyonel)
//   kit  → tabanca dahil, başlangıç için ideal
//
// Tier = tahmini miktar:
//   small  → 3'lü paket
//   medium → 12'li paket
//   large  → 24'lü paket
//   bulk   → 108–216'lı toplu paket
//
// Her hücreye ilgili Shopify variant ID'sini yazın.
// Boş ('') bırakılan hücre için bir üst tier denenir; hepsi boşsa text fallback gösterilir.
const PRODUCT_MAP = {
  can: {
    small:  '46290588631261',
    medium: '46288386621661',
    large:  '46289404395741',
    bulk:   '47079049855197',
  },
  kit: {
    small:  '46290605539549',
    medium: '46288401596637',
    large:  '46289388306653',
    bulk:   '47129088032989',
  },
};

// ── ANALYTICS / TRACKING ─────────────────────────────────────────────────────
const WEBHOOK_URL = 'https://n8n.datontech.com/webhook/energy-calculator-submit';
const WEBHOOK_SECRET = 'e395afb4c272901cca652df547f1a2330048665722fdfda7a82f9d45a3d8d6b3';

const T = (() => {
  let sid = sessionStorage.getItem('ec_sid');
  if (!sid) {
    sid = (crypto.randomUUID && crypto.randomUUID()) ||
          (Date.now() + '-' + Math.random().toString(36).slice(2));
    sessionStorage.setItem('ec_sid', sid);
  }
  return {
    sessionId: sid,
    startedAt: new Date().toISOString(),
    startedAtMs: Date.now(),
    steps: [],                  // {id, durationMs, answer}
    currentStepId: 'q0',
    currentStepStart: Date.now(),
    sent: false,                // terminal flag — prevents duplicate sends
  };
})();

function answerFor(stepId) {
  // 5-question flow: q2=homeSize, q3=bill, q5=symptoms, q6=zones (q7=email)
  const map = { q2:'homeSize', q3:'bill', q5:'symptoms', q6:'zones' };
  const k = map[stepId];
  return k ? ixirproEnergyState.answers[k] : null;
}

function trackNav(newId) {
  T.steps.push({
    id: T.currentStepId,
    durationMs: Date.now() - T.currentStepStart,
    answer: answerFor(T.currentStepId),
  });
  T.currentStepId = newId;
  T.currentStepStart = Date.now();
}

function buildPayload(eventType, extra = {}) {
  // Snapshot the in-progress step without mutating T.steps
  const inProgress = {
    id: T.currentStepId,
    durationMs: Date.now() - T.currentStepStart,
    answer: answerFor(T.currentStepId),
    inProgress: true,
  };
  const allSteps = [...T.steps, inProgress];
  const completedSteps = allSteps.filter(s => {
    if (!/^q\d+$/.test(s.id)) return false;
    const a = s.answer;
    return a != null && (!Array.isArray(a) || a.length > 0);
  }).length;
  return {
    sessionId: T.sessionId,
    eventType,
    startedAt: T.startedAt,
    endedAt: new Date().toISOString(),
    totalDurationMs: Date.now() - T.startedAtMs,
    completedSteps,
    lastStepId: T.currentStepId,
    steps: allSteps,
    answers: ixirproEnergyState.answers,
    ...extra,
  };
}

function sendEvent(eventType, extra = {}) {
  if (T.sent) return;
  // Skip empty abandons — user landed but never interacted
  if (eventType === 'abandon' && T.steps.length === 0 && T.currentStepId === 'q0') return;
  T.sent = true;
  const payload = buildPayload(eventType, extra);

  // fetch + keepalive survives page unload AND supports custom headers
  // (sendBeacon would also survive unload but can't set headers — incompatible with Header Auth)
  fetch(WEBHOOK_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Webhook-Secret': WEBHOOK_SECRET },
    body: JSON.stringify(payload),
    keepalive: true,
  }).catch(() => {});
}

// Abandon detection — visibilitychange fires on tab switch / navigation / mobile background
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') sendEvent('abandon');
});
window.addEventListener('pagehide', () => sendEvent('abandon'));

// 5-question flow: q0(intro) → q2 → qi2 → q3 → qi3 → q5 → qi5 → q6 → qi6 → q7 → q8(results)
// Single-select questions trigger interstitials via this map (multi-select uses continueWithInter)
const FLOW = { 2: 'qi2', 3: 'qi3' };
const KEY_MAP = { 'home-size':'homeSize', 'bill':'bill' };

// ── INTERSTITIAL DATA ────────────────────────────────────────────────────────
const INTER = {
  'home-size': {
    small:  { big:'2 ft²', sub:'of invisible gap area', text:'In a home this size, the combined gap area at frames, sills, and pipe entries can total around 2 sq ft — leaking conditioned air around the clock with no visible signal.', svg:'size' },
    medium: { big:'2–3', sub:'high-impact zones', text:'Homes in this range almost always concentrate their air loss in 2–3 reachable zones. Targeting them with the right IxirPro product beats buying random sealants every season.', svg:'size' },
    large:  { big:'5+', sub:'distinct leak surfaces', text:'More floor area means more envelope to seal. Larger homes typically have five or more separate problem surfaces — start with the zone you can reach in an afternoon.', svg:'size' },
    xlarge: { big:'10+', sub:'individual leak points', text:'Homes over 3,500 sq ft routinely hide a dozen or more individual air-loss points. A systematic, zone-by-zone approach is the only way to make real progress.', svg:'size' },
  },
  'bill': {
    low:   { big:'15%', sub:'typical sealing savings', text:'Even at lower spend, properly sealing one zone with closed-cell foam tends to drop heating and cooling costs by 10–15% — among the highest-ROI improvements you can make.', svg:'bill' },
    med:   { big:'$240', sub:'avg annual savings', text:'Homeowners in this spend bracket who fix their primary leak zone report average savings of $200–$300 a year — usually from a single weekend project.', svg:'bill' },
    high:  { big:'2+', sub:'likely leak zones', text:'Bills in the $200–$350 range almost always trace back to two or more meaningful air-loss zones. Sealing the worst one first creates a visible drop on the next bill.', svg:'bill' },
    vhigh: { big:'30%', sub:'of your bill may be air loss', text:'At this level, air leakage can quietly account for 25–35% of the total. That isn\'t a heating problem — it\'s a sealing problem, and it\'s fixable zone by zone.', svg:'bill' },
  },
};

const INTER_MULTI = {
  symptoms: [
    null,
    { big:'1', sub:'symptom flagged', text:'Even one comfort symptom usually points to a specific zone. Symptoms are often a more precise signal than the bill itself — your home is showing you exactly where it\'s struggling.' },
    { big:'2', sub:'symptoms overlap', text:'Two symptoms at once frequently trace back to the same underlying leak. That makes the fix more targeted, not more complex — one product, one zone, one weekend.' },
    { big:'3+', sub:'strong leak signal', text:'Three or more symptoms is a strong combined signal. Most homeowners at this level find at least one high-priority zone on the first walk-around — and it\'s often reachable.' },
    { big:'4+', sub:'multi-zone exposure', text:'Multiple symptoms across different rooms suggest air-loss isn\'t isolated. A zone-by-zone fix path will be more effective than chasing a single product.' },
  ],
  zones: {
    windows: { big:'#1', sub:'most accessible first fix', text:'Windows and doors are the most reachable starting point — and typically the highest impact per dollar. Foaming the perimeter behind the trim is a real afternoon project.' },
    attic:   { big:'ROI #1', sub:'highest-return single fix', text:'Attic air-sealing is consistently called the single highest-ROI improvement in residential energy auditing. Heat rises — stopping it at the hatch perimeter is the most effective interception point.' },
    garage:  { big:'Often missed', sub:'commonly overlooked zone', text:'The garage-to-living-area boundary is one of the most overlooked sealing zones in U.ixirproEnergyState. homes. It can cost you real money every winter — especially if the door seal or rim joist is unsealed.' },
    pipes:   { big:'Dozens', sub:'of small entry points', text:'Pipe and cable penetrations look harmless individually. Add them up and they often account for a surprising share of total air leakage — each tiny gap stacks against the next.' },
    unsure:  { big:'Start here', sub:'general diagnostic path', text:'Not knowing the exact location is normal. Your result will map the most statistically likely zones for your home size and symptom profile — giving you a prioritised place to start.' },
  }
};

// ── SVG VISUALS FOR INTERSTITIALS ───────────────────────────────────────────
function getInterSvg(key) {
  const svgs = {
    size: `<svg width="120" height="100" viewBox="0 0 120 100" fill="none"><rect x="5" y="25" width="50" height="70" stroke="rgba(255,255,255,.15)" stroke-width="1.5" stroke-dasharray="3 2"/><rect x="5" y="5" width="110" height="90" stroke="rgba(50,74,254,.4)" stroke-width="1.5"/><line x1="5" y1="35" x2="115" y2="35" stroke="rgba(50,74,254,.2)" stroke-width="1" stroke-dasharray="4 3"/><line x1="5" y1="65" x2="115" y2="65" stroke="rgba(50,74,254,.2)" stroke-width="1" stroke-dasharray="4 3"/><line x1="60" y1="5" x2="60" y2="95" stroke="rgba(50,74,254,.2)" stroke-width="1" stroke-dasharray="4 3"/></svg>`,
    bill: `<svg width="120" height="90" viewBox="0 0 120 90" fill="none"><line x1="10" y1="80" x2="110" y2="80" stroke="rgba(255,255,255,.15)" stroke-width="1"/><line x1="10" y1="80" x2="10" y2="10" stroke="rgba(255,255,255,.15)" stroke-width="1"/><rect x="20" y="55" width="18" height="25" fill="rgba(255,255,255,.1)" stroke="rgba(255,255,255,.2)" stroke-width="1"/><rect x="48" y="42" width="18" height="38" fill="rgba(50,74,254,.15)" stroke="rgba(50,74,254,.3)" stroke-width="1"/><rect x="76" y="22" width="18" height="58" fill="rgba(50,74,254,.3)" stroke="rgba(50,74,254,.5)" stroke-width="1.5"/><path d="M29 52 L57 39 L85 19" stroke="rgba(50,74,254,.6)" stroke-width="1.5" stroke-dasharray="3 2"/></svg>`,
    house: `<svg width="120" height="90" viewBox="0 0 120 90" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10 50 L60 10 L110 50" stroke="rgba(50,74,254,.4)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><rect x="20" y="50" width="80" height="35" stroke="rgba(50,74,254,.3)" stroke-width="1.5"/><rect x="30" y="58" width="22" height="16" stroke="rgba(255,255,255,.3)" stroke-width="1.2"/><rect x="68" y="58" width="22" height="16" stroke="rgba(255,255,255,.3)" stroke-width="1.2"/><rect x="48" y="68" width="24" height="17" stroke="rgba(255,255,255,.2)" stroke-width="1.2"/><circle cx="41" cy="58" r="1.5" fill="rgba(50,74,254,.7)"><animate attributeName="cy" values="58;44;30" dur="1.8s" repeatCount="indefinite"/><animate attributeName="opacity" values="0.7;0.3;0" dur="1.8s" repeatCount="indefinite"/></circle><circle cx="79" cy="58" r="1.5" fill="rgba(50,74,254,.7)"><animate attributeName="cy" values="58;44;30" dur="2.1s" begin=".4s" repeatCount="indefinite"/><animate attributeName="opacity" values="0.7;0.3;0" dur="2.1s" begin=".4s" repeatCount="indefinite"/></circle></svg>`,
  };
  return svgs[key] || svgs['house'];
}

// ── RIPPLE ───────────────────────────────────────────────────────────────────
function spawnRipple(btn, e) {
  const r = document.createElement('span');
  r.className = 'ripple-el';
  const rect = btn.getBoundingClientRect();
  r.style.top  = (e.clientY - rect.top)  + 'px';
  r.style.left = (e.clientX - rect.left) + 'px';
  btn.appendChild(r);
  r.addEventListener('animationend', () => r.remove());
}

// ── NAVIGATION ───────────────────────────────────────────────────────────────
let interTimer = null;

function goQ(n) {
  const prevId = idForIndex(ixirproEnergyState.cur);
  const nextId = idForIndex(n);
  trackNav(nextId);

  const dir = n > ixirproEnergyState.cur ? 'right' : 'left';
  document.getElementById(prevId).classList.remove('active','enter-right','enter-left');
  ixirproEnergyState.cur = n;
  const screen = document.getElementById(nextId);
  screen.classList.add('active', 'enter-' + dir);
  setTimeout(() => screen.classList.remove('enter-right','enter-left'), 350);

  // Progress bar — 5-question flow
  const pct = Math.round((visibleStep(n) / 5) * 100);
  const bar = document.getElementById('quiz-progress');
  bar.style.width = pct + '%';

  const counter = screen.querySelector('.quiz-counter');
  if (counter) { counter.classList.remove('updated'); void counter.offsetWidth; counter.classList.add('updated'); }

  if (n === 7) updateRiskPreview();
  if (n === 8) renderResults();

  document.getElementById('quiz-section').scrollIntoView({ behavior:'smooth', block:'start' });
}

// Map logical index to element id (HTML uses q2, q3, q5, q6, q7 as internal IDs; q1 and q4 removed)
function idForIndex(n) {
  const map = { 0:'q0', 2:'q2', 'qi2':'qi2', 3:'q3', 'qi3':'qi3', 5:'q5', 'qi5':'qi5', 6:'q6', 'qi6':'qi6', 7:'q7', 8:'q8' };
  return map[n] || 'q' + n;
}

// Map internal q-id to user-facing step position (1-5) for progress bar math
function visibleStep(n) {
  const map = { 0:0, 2:1, 3:2, 5:3, 6:4, 7:5, 8:5 };
  if (typeof n === 'string') return 0; // interstitials don't update progress bar
  return map[n] !== undefined ? map[n] : 0;
}

function scrollToQuiz() { document.getElementById('quiz-section').scrollIntoView({ behavior:'smooth' }); }

// ── INTERSTITIAL LAUNCH ──────────────────────────────────────────────────────
function launchInter(qiId, nextQ, data) {
  if (!data) { goQ(nextQ); return; }
  trackNav(qiId);

  document.getElementById(qiId + '-visual').innerHTML = getInterSvg(data.svg || 'house');
  document.getElementById(qiId + '-big').textContent   = data.big;
  document.getElementById(qiId + '-bigsub').textContent = data.sub;
  document.getElementById(qiId + '-text').innerHTML    = data.text;

  // Show screen
  const prev = document.getElementById(idForIndex(ixirproEnergyState.cur));
  prev.classList.remove('active','enter-right','enter-left');
  ixirproEnergyState.cur = qiId;
  const screen = document.getElementById(qiId);
  screen.classList.add('active','enter-right');
  setTimeout(() => screen.classList.remove('enter-right'), 350);

  document.getElementById('quiz-section').scrollIntoView({ behavior:'smooth', block:'start' });

  // Timer bar
  const fill = document.getElementById(qiId + '-fill');
  fill.style.transition = 'none';
  fill.style.width = '0%';
  void fill.offsetWidth;
  fill.style.transition = 'width 1.4s linear';
  fill.style.width = '100%';

  if (interTimer) clearTimeout(interTimer);
  interTimer = setTimeout(() => goQ(nextQ), 1500);
}

function skipInter(nextQ) {
  if (interTimer) { clearTimeout(interTimer); interTimer = null; }
  goQ(nextQ);
}

// ── SINGLE SELECT ────────────────────────────────────────────────────────────
function selectSingle(btn, group, nextQ, e) {
  if (e) spawnRipple(btn, e);
  document.querySelectorAll('[data-group="' + group + '"]').forEach(b => b.classList.remove('selected'));
  btn.classList.add('selected');
  const key = KEY_MAP[group] || group;
  ixirproEnergyState.answers[key] = btn.dataset.val;
  const nxtBtn = document.getElementById('nxt' + ixirproEnergyState.cur);
  if (nxtBtn) nxtBtn.disabled = false;

  // Determine interstitial id for current question
  const qiMap = { 2:'qi2', 3:'qi3' };
  const qiId = qiMap[ixirproEnergyState.cur];
  if (qiId) {
    const groupData = INTER[group];
    const data = groupData ? groupData[btn.dataset.val] : null;
    setTimeout(() => launchInter(qiId, nextQ, data), 320);
  } else {
    setTimeout(() => goQ(nextQ), 320);
  }
}

// ── MULTI SELECT ─────────────────────────────────────────────────────────────
function toggleMulti(btn, group, nextQ, e) {
  if (e) spawnRipple(btn, e);
  btn.classList.toggle('selected');
  const parent = btn.closest('.quiz-options');
  const selected = [...parent.querySelectorAll('.q-opt.selected')].map(b => b.dataset.val);
  ixirproEnergyState.answers[group] = selected;
  const nxtBtn = document.getElementById('nxt' + ixirproEnergyState.cur);
  if (nxtBtn) nxtBtn.disabled = selected.length === 0;
}

function continueWithInter(qiId, nextQ) {
  const group = qiId === 'qi5' ? 'symptoms' : 'zones';
  const selected = ixirproEnergyState.answers[group] || [];
  let data = null;

  if (group === 'symptoms') {
    const count = Math.min(selected.filter(x => x !== 'none').length, INTER_MULTI.symptoms.length - 1);
    data = count > 0 ? INTER_MULTI.symptoms[count] : null;
    if (data) data = { ...data, svg: 'size' };
  } else {
    for (const z of ['attic','windows','garage','pipes','unsure']) {
      if (selected.includes(z)) { data = { ...INTER_MULTI.zones[z], svg: 'house' }; break; }
    }
  }

  const nxtBtn = document.getElementById('nxt' + (group === 'symptoms' ? 5 : 6));
  if (nxtBtn) nxtBtn.disabled = true;
  launchInter(qiId, nextQ, data);
}

// ── SCORING ──────────────────────────────────────────────────────────────────
// Rebalanced for 4 active inputs (homeType and billChange removed).
// Max score: 15 + 25 + 30 + 30 = 100. Risk thresholds (25/50/75) remain valid.
function calcScore() {
  let s = 0;
  s += ({ small:4, medium:8, large:12, xlarge:15 }[ixirproEnergyState.answers.homeSize] || 8);                // 0-15
  s += ({ low:5, med:12, high:20, vhigh:25 }[ixirproEnergyState.answers.bill] || 12);                          // 0-25
  s += Math.min(ixirproEnergyState.answers.symptoms.filter(x => x !== 'none').length * 7, 30);                 // 0-30
  s += Math.min(ixirproEnergyState.answers.zones.filter(x => x !== 'unsure').length * 8, 30);                  // 0-30
  return Math.min(s, 100);
}

function getRisk(score) {
  if (score < 25) return {
    level:'low', label:'Low Risk', color:'#4ade80',
    sub:'Your home looks reasonably tight. Keep an eye on weatherstripping and door seals — small issues can be sealed in minutes with a single IxirPro can.'
  };
  if (score < 50) return {
    level:'moderate', label:'Moderate Risk', color:'#fbbf24',
    sub:'You likely have one or two reachable air-leak zones quietly costing you money each month. A targeted IxirPro fix in the right spot makes a measurable difference.'
  };
  if (score < 75) return {
    level:'high', label:'High Risk', color:'#f87171',
    sub:'Several signs point to avoidable air loss in accessible parts of your home. The good news: closed-cell spray foam can knock out the worst zones in a single weekend.'
  };
  return {
    level:'vhigh', label:'Very High Risk', color:'#ef4444',
    sub:'Multiple symptoms and likely zones indicate meaningful air loss hitting your bills and comfort. Start with the highest-priority zone first — your fix path is below.'
  };
}

function updateRiskPreview() {
  document.getElementById('risk-preview-val').textContent = getRisk(calcScore()).label;
}

function checkEmail() {
  const val = document.getElementById('email-input').value;
  const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val) && document.getElementById('consent').checked;
  document.getElementById('btn-submit').disabled = !ok;
}

function submitQuiz() {
  const email = document.getElementById('email-input').value;
  const score = calcScore();
  const risk  = getRisk(score);
  sendEvent('submit', { email, score, risk: risk.label });
  goQ(8);
}

// ── RENDER RESULTS ───────────────────────────────────────────────────────────
function renderResults() {
  const score = calcScore();
  const risk = getRisk(score);

  const lbl = document.getElementById('result-risk-label');
  lbl.textContent = risk.label;
  lbl.className = 'result-risk ' + risk.level;
  document.getElementById('result-sub-text').textContent = risk.sub;

  const fg = document.getElementById('sc-fg');
  fg.style.stroke = risk.color;
  const C = 327;
  setTimeout(() => {
    fg.style.strokeDashoffset = C - (score/100)*C;
    let t=0,start;
    const step = ts => { if(!start)start=ts; t=Math.min(Math.round(((ts-start)/1400)*score),score); document.getElementById('score-n').textContent=t; if(t<score)requestAnimationFrame(step); };
    requestAnimationFrame(step);
  }, 80);

  // Highlight zone diagram
  const selZones = ixirproEnergyState.answers.zones;
  const notSure = selZones.includes('unsure') || selZones.length===0;
  ['attic','windows','garage','pipes'].forEach(z => {
    const el = document.getElementById('hd-'+z);
    if (el) {
      if (notSure || selZones.includes(z)) el.classList.add('lit');
      else el.classList.remove('lit');
    }
  });

  // Solutions — product cards if EC_PRODUCTS is configured, text fallback otherwise
  const solContainer = document.getElementById('result-solutions');
  const hasProducts = renderProductCards(solContainer);
  if (!hasProducts) {
    solContainer.className = 'solutions-list';
    const sols = buildSols(notSure);
    solContainer.innerHTML = sols.map((s,i)=>`
      <div class="sol-row ${i===0?'primary':''}">
        <div class="sol-row-left">
          ${i===0?'<div class="sol-badge">Start here</div>':''}
          <h4>${s.title}</h4><p>${s.desc}</p>
        </div>
        <a href="#" class="btn-sol ${i>0?'ghost':''}">${s.cta}</a>
      </div>`).join('');
  }
}

function pickVariants() {
  const { homeSize, zones } = ixirproEnergyState.answers;
  const realZones = (zones || []).filter(z => z !== 'unsure');
  const notSure = !realZones.length;

  // can → sadece pencere/boru gibi dar noktalarda, tabancasız kullanım
  // kit → geniş alan (tavan arası, garaj) veya emin değilse başlangıç için
  const wideAreaZones = ['attic', 'garage'];
  const needsGun = notSure || realZones.some(z => wideAreaZones.includes(z));
  const type = needsGun ? 'kit' : 'can';

  // Miktar tieri: ev büyüklüğü puanı + zone sayısı puanı
  const sizeScore   = { small:1, medium:2, large:3, xlarge:4 }[homeSize] || 2;
  const zoneScore   = notSure ? 0 : Math.min(realZones.length, 4);
  const total       = sizeScore + zoneScore;
  const primaryTier = total <= 2 ? 'small' : total <= 4 ? 'medium' : total <= 6 ? 'large' : 'bulk';

  const tiers = ['small', 'medium', 'large', 'bulk'];

  // Bir tierdan variant ID bul; boşsa bir üstünü dene
  function resolveId(t) {
    const idx = tiers.indexOf(t);
    for (let i = idx; i < tiers.length; i++) {
      const id = PRODUCT_MAP[type][tiers[i]];
      if (id) return id;
    }
    return null;
  }

  const primaryId   = resolveId(primaryTier);
  const nextTier    = tiers[Math.min(tiers.indexOf(primaryTier) + 1, tiers.length - 1)];
  const secondaryId = primaryTier !== nextTier ? resolveId(nextTier) : null;

  return [primaryId, secondaryId].filter(Boolean);
}

function renderProductCards(container) {
  const catalog = window.EC_PRODUCTS || {};
  const ids = pickVariants();
  const items = ids.map(id => catalog[id]).filter(Boolean);
  if (!items.length) return false;
  container.className = 'ec-prod-grid';
  container.innerHTML = items.map((p, i) => `
    <a href="${p.url}" class="ec-prod-card${i === 0 ? ' primary' : ''}">
      <div class="ec-prod-img">
        ${p.image
          ? `<img src="${p.image}" alt="${p.productTitle}" loading="lazy">`
          : '<span class="ec-prod-img-placeholder">No image</span>'}
      </div>
      <div class="ec-prod-body">
        ${i === 0 ? '<span class="ec-prod-badge">Start here</span>' : ''}
        <div class="ec-prod-title">${p.productTitle}</div>
        ${p.variantTitle && p.variantTitle !== 'Default Title'
          ? `<div class="ec-prod-variant">${p.variantTitle}</div>` : ''}
        <div class="ec-prod-price">${p.price || ''}</div>
      </div>
      <div class="ec-prod-cta">
        Shop Now
        <svg width="12" height="12" viewBox="0 0 16 16" fill="none"><path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
      </div>
    </a>`).join('');
  return true;
}

function buildSols(notSure) {
  if (notSure) return [
    { title:'IxirPro Spray Foam Starter Kit', desc:'Foam, gun, and cleaner — covers the most common accessible leak paths in any home. The right place to start when you don\'t know where to look first.', cta:'Shop Starter Kit' },
    { title:'Window & Door Sealing', desc:'Closed-cell foam for frames, sills, and the gaps behind worn weatherstripping.', cta:'See Window Fixes' },
    { title:'Attic & Upper-Level Sealing', desc:'Spray foam path for attic hatches, recessed lights, and top-plate leaks.', cta:'Shop Attic Solutions' },
  ];
  const z = ixirproEnergyState.answers.zones, paths=[];
  if (z.includes('windows')) paths.push({ title:'Window & Door Sealing', desc:'Foam the perimeter behind trim, around frames, and at door edges — the most accessible high-impact fix.', cta:'See Window & Door Fixes' });
  if (z.includes('attic'))   paths.push({ title:'Attic & Ceiling Air-Sealing', desc:'IxirPro closed-cell foam for hatch perimeters, recessed lights, and top-plate joints.', cta:'Shop Attic Solutions' });
  if (z.includes('garage'))  paths.push({ title:'Garage & Basement Sealing', desc:'Targeted sealing for rim joists, crawl space edges, and the door between garage and living area.', cta:'See Lower-Level Fixes' });
  if (z.includes('pipes'))   paths.push({ title:'Penetration & Gap Sealing', desc:'Focused fixes for pipe runs, cable entries, and utility gaps where small leaks add up fast.', cta:'Seal Penetrations' });
  if (!paths.length) paths.push({ title:'IxirPro Starter Kit', desc:'A practical starting point covering the most common accessible leak paths.', cta:'Start with a Fix Kit' });
  return paths;
}

function toggleFaq(btn) { btn.closest('.faq-item').classList.toggle('open'); }

// ── IP LOCATION + CLIMATE BANNER ─────────────────────────────────────────────
const VERY_COLD = new Set(['AK','MN','ND','SD','MT','WI','MI','ME','VT','NH','WY']);
const COLD      = new Set(['ID','CO','NE','IA','IL','IN','OH','PA','NY','MA','CT','RI','NJ','UT','WA','OR','NM']);
const WARM      = new Set(['FL','TX','AZ','LA','MS','AL','GA','SC','HI','CA']);

function getClimateZone(sc) {
  if (VERY_COLD.has(sc)) return 'very_cold';
  if (COLD.has(sc))      return 'cold';
  if (WARM.has(sc))      return 'warm';
  return 'mixed';
}

const CLIMATE_COPY = {
  very_cold: {
    title: (city, state) => `${city} area homes can lose up to 35% of heat through uninsulated gaps`,
    sub:   (city, state) => `${state}'s severe winters mean poor air sealing costs significantly more here than in milder climates — every unsealed gap compounds each heating month.`,
    stat:  '35%',
    label: 'avg heat loss through uninsulated areas in cold-climate homes'
  },
  cold: {
    title: (city, state) => `${city} homes face 4+ months of heavy heating season`,
    sub:   (city, state) => `Year-round temperature swings in ${state} mean air sealing pays off in both heating and cooling seasons — typical savings after sealing key zones: $250–$400/year.`,
    stat:  '$300+',
    label: 'typical annual savings after sealing key zones'
  },
  mixed: {
    title: (city, state) => `${city} homes experience both heating and cooling exposure`,
    sub:   (city, state) => `Mixed climates like ${state} see air-sealing savings across all seasons — both energy bills and humidity control improve after treatment.`,
    stat:  '20–25%',
    label: 'combined heating + cooling savings in your climate zone'
  },
  warm: {
    title: (city, state) => `${city} homes: unsealed gaps let heat and humidity in`,
    sub:   (city, state) => `Even in ${state}'s warm climate, air gaps increase cooling costs and allow humidity infiltration — leading to mold risk and HVAC overwork.`,
    stat:  '15–20%',
    label: 'cooling savings potential after sealing key zones'
  }
};

function renderLocationBanner(city, state, stateCode) {
  const zone = getClimateZone(stateCode);
  const copy = CLIMATE_COPY[zone];
  document.getElementById('loc-title').textContent      = copy.title(city, state);
  document.getElementById('loc-sub').textContent        = copy.sub(city, state);
  document.getElementById('loc-stat-num').textContent   = copy.stat;
  document.getElementById('loc-stat-label').textContent = copy.label;
  document.getElementById('location-banner-wrap').style.display = 'block';
}

(function fetchLocation() {
  fetch('https://ipapi.co/json/')
    .then(r => r.json())
    .then(d => {
      const cc = d.country_code || '';
      if (cc !== 'US' && cc !== '') return; // only show for US or unknown
      const city  = d.city || '';
      const state = d.region || '';
      const sc    = d.region_code || '';
      if (city && state) renderLocationBanner(city, state, sc);
    })
    .catch(() => {}); // silent fail — banner stays hidden
})();